/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (private-lesson availability calendar: resort
 *              selection, slot fetch/expand/select, hour totals)
 *   extends:   Component (@glimmer/component)
 *   template:  private-calendar.hbs
 *   args:      @maestro, @intComp, @onCompChange, @setOreSelected, @applicationController,
 *              @item, @oreSelectedDate, @maestroTurnoTask, @freePrivatePriceTask,
 *              @localita, @language, @gender, @firstDispo
 *   uses:      services intl, notify, session; moment-timezone;
 *              beebee-base/lib/utility (compattaSlot, notifyUserError);
 *              beebee-base/lib/utility/ajax (beebeeAjaxPost);
 *              beebee-base/lib/get-config; ember-concurrency restartableTask
 *   siblings:  detail-calendar
 *   tests:     private-calendar-selection-test.js,
 *              private-calendar-algorithms-test.js,
 *              private-calendar-network-test.js, private-calendar-test.js
 *   gotchas:
 *     - `_publishOreSelected` exists because the tracked `oreSelected` field
 *       NAME-SHADOWS the `@oreSelected` arg the host passes: under the Classic
 *       component this was a two-way binding, and the Octane conversion to a
 *       one-way arg + local tracked field silently dropped the write-back to
 *       the host controller. Always write through `_publishOreSelected`,
 *       never assign `this.oreSelected` directly (see in-code comment for the
 *       full incident — customers could pick hours with no way to buy them).
 *     - `intComp` getter/setter split (`_intComp`/`_defaultComp`) exists
 *       because writing the derived default through the plain `intComp`
 *       setter permanently shadowed the host's later `@intComp`: `setup()`
 *       runs on first render, before the host's `loadPrivateTask` (which
 *       yields 250ms) seeds its own `intComp`. Only `_intComp` may be set by
 *       a customer pick; `_defaultComp` is the derived fallback and must
 *       always lose to a host-supplied `@intComp`. The effective value is
 *       published back through `@onCompChange` from `getMaestroDispoFull`, so
 *       the host records the resort availability was actually fetched for, and
 *       `onDataChange` drops `_intComp` so a newer host pick wins.
 *     - the gap rules (`blocca_buco_prima`/`blocca_buco_dopo`) arrive from the
 *       SQL dashboard as JSON STRINGS, and "0" means "not configured" — which
 *       is truthy in JS. Always read them through `regolaBucoInMinuti`, never
 *       with a bare truthiness test: steps 3/4 of
 *       `espandiIntervalloSelezionati` used to widen every single-hour pick
 *       unconditionally, so the instructor search asked for two free hours and
 *       returned nothing (POP-202).
 *
 * Fetches and renders the day-by-day private-lesson hour grid
 * (getMaestroDispoFull), expands a click into a contiguous selected block
 * respecting min-duration/gap-blocking rules (espandiIntervalloSelezionati,
 * checkFullDataSet), and republishes the selected slots to the host.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { set } from '@ember/object';
import moment from 'moment-timezone';

import { compattaSlot, notifyUserError } from 'beebee-base/lib/utility';
import { beebeeAjaxPost } from 'beebee-base/lib/utility/ajax';
import { service } from '@ember/service';
import { restartableTask, timeout } from 'ember-concurrency';

import { debug } from '@ember/debug';

import getConfig from 'beebee-base/lib/get-config';

function SlotElement(start_date, end_date, user_id, marked_timespan) {
  this.start_date = start_date;
  this.end_date = end_date;
  this.user_id = user_id;
  this.marked_timespan = marked_timespan;
}

export default class PrivateCalendarComponent extends Component {
  @service intl;
  @service notify;
  @service session;

  @tracked fullDashboard = null;
  @tracked oreSelected = null;
  @tracked dataSet = null;

  /**
   * Publish the chosen hours to the host controller as well as to this
   * component.
   *
   * This field NAME-SHADOWS the `@oreSelected` the host passes down. Under the
   * Classic component this was a TWO-WAY binding, so assigning `this.oreSelected`
   * wrote straight back into `detail.oreSelected` / `ski-instructor.oreSelected`.
   * The Octane conversion turned it into a one-way arg plus this local tracked
   * field, and the write-back was silently lost: the controllers' `oreSelected`
   * stayed at its initial `[]` forever — nothing in either controller ever
   * assigns it. Both route templates gate their whole purchase block on it
   * (`{{#if this.oreSelected}}`, detail.hbs:855 / ski-instructor.hbs:165), so a
   * customer could pick the hours of a private lesson, see them highlighted, and
   * be shown no way to buy them; `freePrivatePriceTask` also early-returns on the
   * empty list, so no price was ever calculated either.
   *
   * One-way data flow is kept: the host supplies `@setOreSelected` and owns the
   * value, exactly as it already does for `@applicationController.date`.
   */
  _publishOreSelected(value) {
    this.oreSelected = value;
    this.args.setOreSelected?.(value);
  }
  // The resort (comprensorio) the calendar asks availability for, in precedence
  // order: what the customer picked HERE, else what the host passes, else the
  // default this component derives.
  //
  // The derived default needs its own field. It used to be written through the
  // `intComp` setter, i.e. into `_intComp` — and `setup()` runs during the first
  // render, BEFORE the host controller's `loadPrivateTask` (which yields 250 ms)
  // has seeded its own `intComp`. So `_intComp` was always populated with the
  // tenant's first private resort, and from that moment `@intComp` was shadowed
  // for the rest of the component's life: every later resort change made by the
  // host was silently dropped. In ghost mode — where the resort buttons are the
  // ONLY filter a customer gets — the button flipped to `active` and the hour
  // calendar went on loading the default resort's availability, so the customer
  // booked hours somewhere they had not chosen.
  @tracked _intComp = null;
  @tracked _defaultComp = null;
  get intComp() {
    return this._intComp ?? this.args.intComp ?? this._defaultComp;
  }
  set intComp(value) {
    this._intComp = value;
  }
  @tracked comp = null;
  @tracked free_slot_present = null;

  // Stashed root element
  _el = null;

  @action
  setup(element) {
    this._el = element;
    this.setDefaultComp();
    this.fullDashboard = null;
    this.getMaestroDispoFull.perform();
  }

  @action
  teardown(/*element*/) {
    this._el = null;
  }

  @action
  onDataChange() {
    if (this.args.maestroTurnoTask) {
      return;
    }

    // A host-driven reload (resort/language/date button, new product, new
    // instructor) is a NEWER customer action than an older pick made in this
    // component, so drop the local pick and let `@intComp` win again. Without
    // this the host's resort button flipped to `active` while the hour grid kept
    // loading the resort picked here — the customer booked hours somewhere they
    // had stopped choosing.
    this._intComp = null;

    this.setDefaultComp();
    this.fullDashboard = null;
    this.getMaestroDispoFull.perform();
  }

  setDefaultComp() {
    if (this.intComp) {
      return;
    }

    // Writes the DERIVED default, never the customer's pick: a value produced
    // here must lose to a `@intComp` the host supplies later.
    if (Array.isArray(this.args.maestro?.localita)) {
      this._defaultComp = this.args.maestro.localita[0];
    } else if (Array.isArray(this.session.option_field?.comprensori)) {
      const cPrivate = this.session.option_field.comprensori.filter(
        (item) => item.ecommerce_private === true
      );

      const def = cPrivate.find((i) => i.default === true);
      const firstComp = cPrivate[0]?.name;

      this._defaultComp = def?.name || firstComp;
    }
  }

  get startDayH() {
    return moment(this.args.applicationController.date).format('DD/MM/YYYY');
  }

  get endDayH() {
    return moment(this.args.applicationController.date)
      .add(this.nGiorniPrivateCalendar, 'days')
      .format('DD/MM/YYYY');
  }

  get mezzore_giornata() {
    return this.session.option_field?.mezzore_giornata || 15;
  }

  get avvisi_ore_private() {
    return {
      warning: this.session.option_field?.ecommerce_avvisi_ore_private?.warning || 5,
      alert: this.session.option_field?.ecommerce_avvisi_ore_private?.alert || 3,
    };
  }

  get ora_inizio_giornata() {
    return this.session.option_field?.ora_inizio_giornata || 9;
  }

  get ecommerce_ore_piene() {
    return this.session.option_field?.ecommerce_ore_piene || null;
  }

  get ecommerce_vendi_solo_ore_piene() {
    return this.session.option_field?.ecommerce_vendi_solo_ore_piene;
  }

  get ecommerce_riempi_buchi() {
    return this.session.option_field?.ecommerce_riempi_buchi || false;
  }

  get ecommerce_blocca_buchi() {
    const ecommerce_blocca_buchi = this.session.option_field?.ecommerce_blocca_buchi;

    if (ecommerce_blocca_buchi === true) {
      return this.ecommerce_ore_piene ? 60 : 30;
    }

    return ecommerce_blocca_buchi || false;
  }

  get ecommerce_private_ghost() {
    return this.session.option_field?.ecommerce_provate_ghost || false;
  }

  get ecommerce_ore_intervallo() {
    return this.session.option_field?.ecommerce_ore_intervallo || [];
  }

  get totOreSelezionate() {
    if (Array.isArray(this.oreSelected)) {
      return this.calcolaNumeroOreSelected(this.oreSelected);
    }

    return null;
  }

  get showBaseline() {
    return this.args.maestroTurnoTask && this.totOreSelezionate && !this.args.maestro;
  }

  get ecommerce_private_fixed_slot() {
    const optionField = this.session.option_field;

    if (!optionField) {
      return false;
    }

    const private_ids = optionField.ecommerce_private_fixed_slot_ids || [];
    const itemId = this.args.item?.id;

    if (private_ids[itemId]) {
      return private_ids[itemId];
    }

    return optionField.ecommerce_private_fixed_slot || false;
  }

  get listLocalita() {
    return this.args.maestro?.localita || this.args.localita?.map((item) => item.localita);
  }

  get nGiorniPrivateCalendar() {
    return this.session.option_field?.ecommerce_numero_giorni_calendario || 7;
  }

  get showResults() {
    const isRunning = this.getMaestroDispoFull?.isRunning;
    const giorni = this.fullDashboard?.giorni;

    if (isRunning) {
      return true;
    }

    return Array.isArray(giorni) && giorni.length;
  }

  @restartableTask
  *getMaestroDispoFull(next = false) {
    yield timeout(300);

    // Tell the host which resort this fetch is about to ask availability for.
    // It is resolved HERE (`_intComp` pick, else `@intComp`, else `_defaultComp`)
    // and used to stay here, so the cart could record a different resort from the
    // one the hours were picked on — or none at all on a deep link, where nothing
    // seeded the host (POP-109). After the yield, so it is outside the render
    // transaction; the equality guard stops it bouncing once the host has it.
    if (this.intComp && this.intComp !== this.args.intComp) {
      this.args.onCompChange?.(this.intComp);
    }

    var self = this;

    this.dataSet = null;

    const data = this._buildEvents();

    if (next) {
      // Riprende dal giorno più avanzato già caricato in fullDashboard, non dalla
      // data base: il primo load può estendere `fine` oltre la finestra base (ramo
      // oreSelectedDate) e ricalcolare da base+offset farebbe tornare indietro.
      let lastDay = null;
      const giorniCaricati = this.fullDashboard?.giorni;
      if (Array.isArray(giorniCaricati)) {
        giorniCaricati.forEach((g) => {
          if (!g.giorno) {
            return;
          }
          const d = moment(g.giorno);
          if (!lastDay || d.isAfter(lastDay)) {
            lastDay = d;
          }
        });
      }
      const start = lastDay || (data.fine ? moment(data.fine, 'YYYY-MM-DD') : moment());
      data.inizio = start.clone().add(1, 'days').format('YYYY-MM-DD');
      data.fine = start.clone().add(this.nGiorniPrivateCalendar, 'days').format('YYYY-MM-DD');
    } else if (Array.isArray(this.args.oreSelectedDate) && this.args.oreSelectedDate.length > 0) {
      // Estrai tutte le start_date ed end_date
      const startDates = this.args.oreSelectedDate.map((event) => moment(event.start_date));
      const endDates = this.args.oreSelectedDate.map((event) => moment(event.end_date));

      // Trova il minimo di start_date e il massimo di end_date
      const minStartDate = moment.min(startDates);
      const maxEndDate = moment.max(endDates);

      if (minStartDate) {
        if (!data.inizio) {
          data.inizio = moment().format('YYYY-MM-DD');
        }

        minStartDate.subtract(1, 'days');
        if (moment(data.inizio, 'YYYY-MM-DD').isAfter(minStartDate)) {
          data.inizio = minStartDate.clone().format('YYYY-MM-DD');
        }
      }

      if (maxEndDate) {
        if (!data.fine) {
          data.fine = moment().add(this.nGiorniPrivateCalendar, 'days').format('YYYY-MM-DD');
        }

        maxEndDate.add(1, 'days');
        if (moment(data.fine, 'YYYY-MM-DD').isBefore(maxEndDate)) {
          data.fine = maxEndDate.clone().format('YYYY-MM-DD');
        }
      }
    }

    try {
      const events = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_private_dashboard_week`,
        null,
        data
      );

      const timeZone = 'Europe/Rome';

      const concEvent = {
        giorni: [...((next && this.fullDashboard?.giorni) || []), ...(events?.giorni || [])],
      };

      if (Array.isArray(concEvent.giorni)) {
        concEvent.giorni.forEach((obj) => {
          if (Array.isArray(obj.intervalli)) {
            obj.intervalli.forEach((intervallo) => {
              const inizio = moment.tz(intervallo.start_date, timeZone);
              const fine = moment.tz(intervallo.end_date, timeZone);
              const durata = fine.diff(inizio, 'minutes');
              const libera = intervallo.dispo > 0;
              const boxClass =
                intervallo.dispo > 0 && this.ecommerce_private_ghost
                  ? intervallo.dispo < this.avvisi_ore_private.alert
                    ? 'libere-3'
                    : intervallo.dispo < this.avvisi_ore_private.warning
                      ? 'libere-5'
                      : null
                  : null;

              (self.args.oreSelectedDate || []).forEach((slot) => {
                const inizioSlot = moment.tz(slot.start_date, timeZone);
                const fineSlot = moment.tz(slot.end_date, timeZone);

                const inSlot =
                  inizio.isBetween(inizioSlot, fineSlot, null, '[]') &&
                  fine.isBetween(inizioSlot, fineSlot, null, '[]');

                if (inSlot) {
                  set(intervallo, 'selected', true);
                }
              });

              Object.assign(intervallo, {
                hourHuman: inizio.format('HH:mm'),
                hourHuman_end: fine.format('HH:mm'),
                durata,
                moment: inizio,
                moment_end: fine,
                libera,
                boxClass,
              });
            });
          }
        });
      }

      this.fullDashboard = concEvent;

      this.checkFullDataSet();

      if (typeof this.args.freePrivatePriceTask === 'object') {
        this.args.freePrivatePriceTask.perform();
      }

      const oreSelected = this.calcolaOreSelected();
      this._publishOreSelected(oreSelected);
    } catch (e) {
      self.free_slot_present = false;
      debug(e);
      notifyUserError(this.notify, e, this.intl);
    }
  }

  _buildEvents() {
    const inizio = this.args.applicationController.date || this.args.firstDispo;
    const fine = moment(inizio).add(this.nGiorniPrivateCalendar, 'days');
    const contactId = this.args.maestro?.id;
    const formattedInizio =
      inizio && inizio !== 'null' ? moment(inizio).format('YYYY-MM-DD') : null;
    const formattedFine = fine && fine !== 'null' ? moment(fine).format('YYYY-MM-DD') : null;
    const productId = this.args.item.product_id;
    const userId = this.session.me?.id;
    const currentTime = moment().format('HHmmss');

    return {
      lang: this.args.language || null,
      gen: this.args.gender || null,
      contact_id: contactId,
      inizio: formattedInizio,
      fine: formattedFine,
      comp: this.intComp,
      product_id: productId,
      ore_piene: this.ecommerce_ore_piene,
      user_id: userId,
      _t: currentTime,
      access_token: this.session.token,
    };
  }

  isPrimoIntervalloSelezionato(intervalli, intervalloSelezionato) {
    if (intervalli.length === 0) {
      return false;
    }

    const indiceSelezionato = intervalli.indexOf(intervalloSelezionato);
    if (indiceSelezionato < 0 || indiceSelezionato >= intervalli.length) {
      return false;
    }

    if (indiceSelezionato == 0 || !intervalli[indiceSelezionato - 1].selected) {
      return true;
    }

    return false;
  }

  getPrimoIntervalloSelezionato(intervalli, intervalloSelezionato) {
    if (intervalli.length === 0) {
      return intervalloSelezionato;
    }

    const indiceSelezionato = intervalli.indexOf(intervalloSelezionato);
    if (indiceSelezionato <= 0 || indiceSelezionato >= intervalli.length) {
      return intervalloSelezionato;
    }

    let primoIntervalloSelezionato = intervalloSelezionato;
    let indiceIndietro = indiceSelezionato - 1;

    while (indiceIndietro >= 0) {
      const intervalloIndietro = intervalli[indiceIndietro];

      if (intervalloIndietro.selected) {
        primoIntervalloSelezionato = intervalloIndietro;
        indiceIndietro--;
      } else {
        break;
      }
    }

    return primoIntervalloSelezionato;
  }

  contaOreIntervallo(intervalli, intervalloSelezionato) {
    const oreDopo = this.getDurataIntervalliSelectedDopo(intervalli, intervalloSelezionato);
    return intervalloSelezionato.durata + oreDopo;
  }

  getDurataIntervalliLiberiPrima(intervalli, intervalloSelezionato) {
    if (intervalli.length === 0) {
      return 0;
    }

    const indiceSelezionato = intervalli.indexOf(intervalloSelezionato);
    if (indiceSelezionato <= 0 || indiceSelezionato >= intervalli.length) {
      return 0;
    }

    let startDispoNonSelezionata = intervalloSelezionato;
    let indiceIndietro = indiceSelezionato - 1;
    let primaLibera = false;

    while (indiceIndietro >= 0) {
      const intervalloIndietro = intervalli[indiceIndietro];

      if (intervalloIndietro.selected) {
        if (primaLibera) {
          break;
        } else {
          indiceIndietro--;
        }
      } else if (
        intervalloIndietro.libera &&
        !intervalloIndietro.selected &&
        intervalloIndietro.moment_end.isSameOrAfter(intervalli[indiceIndietro + 1].moment)
      ) {
        primaLibera = true;
        startDispoNonSelezionata = intervalloIndietro;
        indiceIndietro--;
      } else {
        break;
      }
    }

    return intervalloSelezionato.moment.diff(startDispoNonSelezionata.moment, 'minutes');
  }

  getDurataIntervalliLiberiDopo(intervalli, intervalloSelezionato) {
    if (intervalli.length === 0) {
      return 0;
    }

    const indiceSelezionato = intervalli.indexOf(intervalloSelezionato);
    if (indiceSelezionato < 0 || indiceSelezionato >= intervalli.length - 1) {
      return 0;
    }

    let endDispoNonSelezionata = intervalloSelezionato;
    let endDispoSelezionata = intervalloSelezionato;
    let indiceAvanti = indiceSelezionato + 1;
    let primaLibera = false;

    while (indiceAvanti < intervalli.length) {
      const intervalloAvanti = intervalli[indiceAvanti];

      if (intervalloAvanti.selected) {
        if (primaLibera) {
          break;
        } else {
          endDispoNonSelezionata = intervalloAvanti;
          endDispoSelezionata = intervalloAvanti;
          indiceAvanti++;
        }
      } else if (
        intervalloAvanti.libera &&
        !intervalloAvanti.selected &&
        intervalloAvanti.moment.isSameOrBefore(intervalli[indiceAvanti - 1].moment_end)
      ) {
        primaLibera = true;
        endDispoNonSelezionata = intervalloAvanti;
        indiceAvanti++;
      } else {
        break;
      }
    }

    return endDispoNonSelezionata.moment_end.diff(endDispoSelezionata.moment_end, 'minutes');
  }

  getDurataIntervalliSelectedDopo(intervalli, intervalloSelezionato) {
    if (intervalli.length === 0) {
      return 0;
    }

    const indiceSelezionato = intervalli.indexOf(intervalloSelezionato);
    if (indiceSelezionato < 0 || indiceSelezionato >= intervalli.length - 1) {
      return 0;
    }

    let endDispoNonSelezionata = intervalloSelezionato;
    let endDispoSelezionata = intervalloSelezionato;
    let indiceAvanti = indiceSelezionato + 1;
    let primaLibera = false;

    while (indiceAvanti < intervalli.length) {
      const intervalloAvanti = intervalli[indiceAvanti];

      if (!intervalloAvanti.selected) {
        if (primaLibera) {
          break;
        } else {
          endDispoNonSelezionata = intervalloAvanti;
          endDispoSelezionata = intervalloAvanti;
          indiceAvanti++;
        }
      } else if (
        intervalloAvanti.libera &&
        intervalloAvanti.selected &&
        intervalloAvanti.moment.isSameOrBefore(intervalli[indiceAvanti - 1].moment_end)
      ) {
        primaLibera = true;
        endDispoNonSelezionata = intervalloAvanti;
        indiceAvanti++;
      } else {
        break;
      }
    }

    return endDispoNonSelezionata.moment_end.diff(endDispoSelezionata.moment_end, 'minutes');
  }

  checkFullDataSet() {
    if (!this.ecommerce_ore_intervallo) {
      return;
    }

    let minimoNonSoddisfatto = false;
    let bloccoVuotoPrima = false;
    let bloccoVuotoDopo = false;
    let bloccoOrePiene = false;

    for (let i = 0; i < this.fullDashboard.giorni.length; i++) {
      const giorno = this.fullDashboard.giorni[i];

      if (Array.isArray(giorno.intervalli)) {
        for (let j = 0; j < giorno.intervalli.length; j++) {
          const intervallo = giorno.intervalli[j];

          if (intervallo.selected) {
            const isPrimo = this.isPrimoIntervalloSelezionato(giorno.intervalli, intervallo);

            if (isPrimo) {
              const oreBlocco = this.contaOreIntervallo(giorno.intervalli, intervallo);
              const durataLiberiPrima = this.getDurataIntervalliLiberiPrima(
                giorno.intervalli,
                intervallo
              );
              const durataLiberiDopo = this.getDurataIntervalliLiberiDopo(
                giorno.intervalli,
                intervallo
              );

              if (oreBlocco < intervallo.min_intervalli) {
                minimoNonSoddisfatto = {
                  giorno: giorno,
                  intervallo: intervallo,
                };
              }

              const regolaBucoPrima = this.regolaBucoInMinuti(intervallo.blocca_buco_prima);
              const regolaBucoDopo = this.regolaBucoInMinuti(intervallo.blocca_buco_dopo);

              if (regolaBucoPrima && durataLiberiPrima && durataLiberiPrima <= regolaBucoPrima) {
                bloccoVuotoPrima = {
                  giorno: giorno,
                  intervallo: intervallo,
                  durata: durataLiberiPrima,
                };
              }

              if (regolaBucoDopo && durataLiberiDopo && durataLiberiDopo <= regolaBucoDopo) {
                bloccoVuotoDopo = {
                  giorno: giorno,
                  intervallo: intervallo,
                  durata: durataLiberiDopo,
                };
              }

              if (!bloccoOrePiene) {
                const isOrePiene = this.isFullHour(oreBlocco);

                if (!isOrePiene) {
                  bloccoOrePiene = {
                    giorno: giorno,
                    intervallo: intervallo,
                  };
                }
              }
            }
          }
        }
      }
    }

    if (minimoNonSoddisfatto) {
      this.azzeroRiga(minimoNonSoddisfatto.giorno.intervalli);
      this.notify.warning(
        `Ore minime non soddisfatte ${minimoNonSoddisfatto.intervallo.moment.format(
          'DD MMM HH:mm'
        )}`
      );
    }

    if (bloccoVuotoPrima) {
      this.azzeroRiga(bloccoVuotoPrima.giorno.intervalli);
      this.notify.warning(`Blocco vuoto prima di ${bloccoVuotoPrima.durata}`);
    }

    if (bloccoVuotoDopo) {
      this.azzeroRiga(bloccoVuotoDopo.giorno.intervalli);
      this.notify.warning(`Blocco vuoto dopo di ${bloccoVuotoDopo.durata}`);
    }

    if (bloccoOrePiene) {
      this.azzeroRiga(bloccoOrePiene.giorno.intervalli);
      this.notify.warning(`Puoi acquistare solo ore piene`);
    }
  }

  isFullHour(minutes) {
    return !this.ecommerce_vendi_solo_ore_piene || minutes % 60 === 0;
  }

  /**
   * `blocca_buco_prima` / `blocca_buco_dopo` reach the client as JSON *strings*
   * produced by `scuolesci_individuali_ecommerce_week`, and that function emits
   * the literal "0" for every hour the school has NOT configured. "0" is truthy
   * in JavaScript, so a plain truthiness test reads "not configured" as
   * "configured": every gap rule must go through this numeric normalisation.
   * Returns 0 (= no rule) for null, "", "0" and anything non-numeric.
   */
  regolaBucoInMinuti(valore) {
    const minuti = Number(valore);

    return Number.isFinite(minuti) && minuti > 0 ? minuti : 0;
  }

  /**
   * The selection was widened on the user's behalf to avoid leaving an
   * unbookable gap. Say so — silently turning one chosen hour into two is what
   * made the availability search return no instructor at all (POP-202).
   */
  avvisaIntervalloAggiunto(intervallo) {
    const from =
      intervallo.hourHuman || moment(intervallo.start_date).tz('Europe/Rome').format('HH:mm');
    const to =
      intervallo.hourHuman_end || moment(intervallo.end_date).tz('Europe/Rome').format('HH:mm');

    this.notify.warning(this.intl.t('private calendar gap hour added', { from, to }));
  }

  espandiIntervalloSelezionati(intervalli, intervalloSelezionato) {
    if (!intervalli || !intervalloSelezionato) {
      return;
    }

    const selezionati = [intervalloSelezionato];
    let durataTotale = intervalloSelezionato.durata || 0;
    const minRichiesto = intervalloSelezionato.min_intervalli || 0;

    const indiceCorrente = intervalli.indexOf(intervalloSelezionato);

    if (indiceCorrente === -1) {
      return;
    }

    let indiceAvanti = indiceCorrente + 1;
    let indiceIndietro = indiceCorrente - 1;

    // --- 1) Espansione avanti obbligatoria ---
    while (indiceAvanti < intervalli.length && durataTotale < minRichiesto) {
      const avanti = intervalli[indiceAvanti];
      const ultimo = selezionati[selezionati.length - 1];

      if (avanti && avanti.libera && avanti.start_date === ultimo.end_date) {
        selezionati.push(avanti);
        durataTotale += avanti.durata || 0;
        indiceAvanti++;
      } else {
        break;
      }
    }

    // --- 2) Espansione indietro obbligatoria ---
    while (indiceIndietro >= 0 && durataTotale < minRichiesto) {
      const indietro = intervalli[indiceIndietro];
      const primo = selezionati[0];

      if (indietro && indietro.libera && indietro.end_date === primo.start_date) {
        selezionati.unshift(indietro);
        durataTotale += indietro.durata || 0;
        indiceIndietro--;
      } else {
        break;
      }
    }

    // --- 3) Controllo BUCO DOPO ---
    // Absorb the single free slot left after the block ONLY when the school
    // configured `blocca_buco_dopo` and that leftover is small enough for
    // checkFullDataSet to reject it (`durataLiberiDopo <= blocca_buco_dopo`,
    // same criterion). Without the rule the widening was pure loss: one chosen
    // hour became two, so the instructor search asked for two free hours and
    // came back empty (POP-202).
    // The rule is read from the hour the customer clicked, exactly like
    // `min_intervalli` above. With a per-hour configuration that disagrees
    // across the block, checkFullDataSet remains the backstop: it clears the
    // row with a visible "Blocco vuoto" warning rather than failing silently.
    const bloccaBucoDopo = this.regolaBucoInMinuti(intervalloSelezionato.blocca_buco_dopo);
    const lastIndex = intervalli.indexOf(selezionati[selezionati.length - 1]);

    if (bloccaBucoDopo && lastIndex !== -1 && lastIndex < intervalli.length - 1) {
      for (let i = lastIndex + 1; i < intervalli.length; i++) {
        const next = intervalli[i];
        const ultimo = selezionati[selezionati.length - 1];
        const contiguo = next && next.start_date === ultimo.end_date;

        if (next && next.libera && contiguo) {
          const nextAfter = intervalli[i + 1];

          if (!nextAfter || !nextAfter.libera) {
            const buco = Number(next.durata) || 0;

            if (buco > 0 && buco <= bloccaBucoDopo) {
              selezionati.push(next);
              durataTotale += buco;
              this.avvisaIntervalloAggiunto(next);
            }

            break;
          }
        } else {
          break;
        }
      }
    }

    // --- 4) Controllo BUCO PRIMA ---
    // Mirror of step 3, gated on `blocca_buco_prima` (see the note above).
    const bloccaBucoPrima = this.regolaBucoInMinuti(intervalloSelezionato.blocca_buco_prima);
    const firstIndex = intervalli.indexOf(selezionati[0]);

    if (bloccaBucoPrima && firstIndex > 0) {
      const prev1 = intervalli[firstIndex - 1];
      const prev2 = intervalli[firstIndex - 2];
      const primo = selezionati[0];

      if (prev1) {
        const contiguoPrev = prev1.end_date === primo.start_date;

        if (prev1.libera && contiguoPrev && (!prev2 || !prev2.libera)) {
          const buco = Number(prev1.durata) || 0;

          if (buco > 0 && buco <= bloccaBucoPrima) {
            selezionati.unshift(prev1);
            durataTotale += buco;
            this.avvisaIntervalloAggiunto(prev1);
          }
        }
      }
    }

    // --- 5) Imposta selected=true ---
    selezionati.forEach((intervallo) => {
      if (intervallo) {
        set(intervallo, 'selected', true);
      }
    });
  }

  calcolaNumeroOreSelected(eventi) {
    let totaleOre = 0;

    eventi.forEach((evento) => {
      const start_date = moment(evento.start_date);
      const end_date = moment(evento.end_date);
      const diff = end_date.diff(start_date, 'hours', true);
      totaleOre += diff;
    });

    return this.convertiOreInFormatoOrario(totaleOre);
  }

  convertiOreInFormatoOrario(ore) {
    const oreInteri = Math.floor(ore);
    const minutiDecimali = (ore - oreInteri) * 60;
    const minutiInteri = Math.round(minutiDecimali);

    let risultato = '';

    if (oreInteri > 0) {
      risultato += `${oreInteri}`;
    }

    if (minutiInteri > 0) {
      if (risultato !== '') {
        risultato += '½';
      } else {
        risultato += `${minutiInteri}/60`;
      }
    }

    return risultato;
  }

  @action
  setComp(comp) {
    this._publishOreSelected([]);
    this.fullDashboard = null;
    if (this.args.maestroTurnoTask) {
      this.comp = comp;
    }
    this.intComp = comp;
    this.getMaestroDispoFull.perform();
  }

  @action
  uncheckAll() {
    this._publishOreSelected([]);

    if (Array.isArray(this.fullDashboard?.giorni)) {
      this.fullDashboard.giorni.forEach(function (g) {
        g.intervalli.forEach((i) => set(i, 'selected', false));
      });
    }

    const target = document.getElementById('divCalendarioScuolaMobile');

    if (target) {
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY;

      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  @action
  requestMaeMail(/*side = 'right'*/) {
    if (this.args.applicationController.showRichiediInfo) {
      const msg = `Buongiorno, vorrei chiedere se è disponibile un maestro ${
        this.intComp ? `a ${this.intComp}` : 'in qualunque località'
      } ${this.args.language ? `che parli ${this.args.language}` : ''} ${
        this.args.gender ? `possibilimente di sesso ${this.args.gender}` : ''
      }
Vi ringrazio`;
      set(this.args.applicationController, 'contact_message', msg);
      set(this.args.applicationController, 'noDispoOnline', true);
    }
  }

  @action
  selectOra(ora, giorno) {
    var add = ora.selected ? false : true;

    set(ora, 'moment', moment(ora.start_date).tz('Europe/Rome'));
    set(ora, 'hourHuman', moment(ora.start_date).tz('Europe/Rome').format('HH:mm'));
    set(ora, 'hourHuman_end', moment(ora.end_date).tz('Europe/Rome').format('HH:mm'));

    if (!ora.libera) {
      if (this.args.applicationController.showRichiediInfo) {
        const mae = this.args.maestro?.name
          ? `${this.args.maestro.name} è disponibile`
          : `c'è disponibilità`;
        const msg = `Buongiorno, vorrei chiedere se ${mae} il ${ora.moment.format('DD/MM/YYYY')} ${
          ora.hourHuman_end
            ? `dalle ${ora.hourHuman} alle ${ora.hourHuman_end}`
            : `alle ${ora.hourHuman}`
        } a ${this.intComp} per ${this.args.item?.name ?? 'la lezione'}.
Vi ringrazio`;
        set(this.args.applicationController, 'contact_message', msg);
        set(this.args.applicationController, 'noDispoOnline', true);
      }
      return false;
    }

    set(ora, 'selected', add);

    if (add) {
      this.espandiIntervalloSelezionati(
        giorno.intervalli,
        this.getPrimoIntervalloSelezionato(giorno.intervalli, ora)
      );
    }

    this.checkFullDataSet();

    const oreSelected = this.calcolaOreSelected();
    this._publishOreSelected(oreSelected);
    if (typeof this.args.freePrivatePriceTask === 'object') {
      this.args.freePrivatePriceTask.perform();
    }
  }

  @action
  cercaMaestroTurno() {
    if (typeof this.args.maestroTurnoTask === 'object') {
      this.args.maestroTurnoTask.perform();
    }
  }

  calcolaOreSelected() {
    const self = this;
    let oreSelected = [];
    const minutesToAdd = 30;

    this.fullDashboard.giorni.forEach((giorno) => {
      if (!Array.isArray(giorno.intervalli)) {
        return;
      }
      const tmpIntSel = giorno.intervalli.filter((item) => item.selected === true);

      tmpIntSel.forEach((ora) => {
        const start = moment(ora.moment);

        const slotElement = new SlotElement(
          moment(start).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mm'),
          ora.moment_end
            ? moment(ora.moment_end).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mm')
            : moment(start)
                .tz('Europe/Rome', true)
                .add(minutesToAdd, 'm')
                .format('YYYY-MM-DDTHH:mm'),
          self.args.maestro?.id || null,
          1
        );

        oreSelected.push(slotElement);
      });
    });

    oreSelected = compattaSlot(oreSelected, SlotElement, this.ecommerce_ore_piene ? 60 : 30);

    oreSelected.forEach((o) => set(o, 'location', this.intComp));

    return oreSelected;
  }

  azzeroRiga(riga) {
    riga.forEach((i) => set(i, 'selected', false));
  }
}
